import React, { useState, useEffect } from 'react';
import { Card, Switch, Typography, Space, Button, Spin, Divider, message } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import {
    useGetCookieConsentQuery,
    useSaveCookieConsentMutation,
    useGetSignupConfigQuery,
} from '../../services/nestjs-base-api';

const { Title, Text, Paragraph } = Typography;

const COOKIE_CONSENT_SESSION_KEY = 'cookie_consent_session_id';

/**
 * Cookie/Privacy Settings component for user profile
 * Allows users to manage their cookie consent preferences
 */
const CookieSettings: React.FC = () => {
    const { user } = useAuth();
    const sessionId = localStorage.getItem(COOKIE_CONSENT_SESSION_KEY) || undefined;

    // Check if feature is enabled
    const { data: config, isLoading: configLoading } = useGetSignupConfigQuery();

    // Get current consent
    const { data: consentData, isLoading: consentLoading, refetch } = useGetCookieConsentQuery(
        { sessionId, jwtToken: user?.jwtToken },
        { skip: !config?.cookieConsentEnabled }
    );

    const [saveConsent, { isLoading: saving }] = useSaveCookieConsentMutation();

    // Local state for switches
    const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
    const [marketingEnabled, setMarketingEnabled] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    // Sync local state with fetched data
    useEffect(() => {
        if (consentData) {
            setAnalyticsEnabled(consentData.analyticsConsent);
            setMarketingEnabled(consentData.marketingConsent);
        }
    }, [consentData]);

    // Track changes
    useEffect(() => {
        if (consentData) {
            const changed =
                analyticsEnabled !== consentData.analyticsConsent ||
                marketingEnabled !== consentData.marketingConsent;
            setHasChanges(changed);
        }
    }, [analyticsEnabled, marketingEnabled, consentData]);

    const handleSave = async () => {
        try {
            await saveConsent({
                analyticsConsent: analyticsEnabled,
                marketingConsent: marketingEnabled,
                sessionId,
                jwtToken: user?.jwtToken,
            }).unwrap();
            message.success('Privacy preferences saved successfully');
            setHasChanges(false);
            refetch();
        } catch (error) {
            console.error('Failed to save cookie preferences:', error);
            message.error('Failed to save privacy preferences');
        }
    };

    const isLoading = configLoading || consentLoading;

    // Feature not enabled
    if (!configLoading && !config?.cookieConsentEnabled) {
        return (
            <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Title level={4}>
                            <SafetyOutlined style={{ marginRight: 8 }} />
                            Privacy Settings
                        </Title>
                        <Paragraph type="secondary">
                            Cookie consent management is not currently enabled for this platform.
                        </Paragraph>
                    </div>
                </Space>
            </Card>
        );
    }

    if (isLoading) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>Loading privacy settings...</div>
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                    <Title level={4}>
                        <SafetyOutlined style={{ marginRight: 8 }} />
                        Privacy Settings
                    </Title>
                    <Paragraph type="secondary">
                        Manage your cookie preferences. These settings control how we collect
                        and use data about your interactions with the platform.
                    </Paragraph>
                </div>

                <Divider style={{ margin: '16px 0' }} />

                {/* Essential Cookies */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <Text strong>Essential Cookies</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            Required for the platform to function properly. These cookies enable core
                            functionality such as security, authentication, and session management.
                            Cannot be disabled.
                        </Text>
                    </div>
                    <Switch checked disabled style={{ marginLeft: 16 }} />
                </div>

                <Divider style={{ margin: '16px 0' }} />

                {/* Analytics Cookies */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <Text strong>Analytics Cookies</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            Help us understand how you interact with the platform by collecting and
                            reporting usage information. This data helps us improve our services
                            and user experience.
                        </Text>
                    </div>
                    <Switch
                        checked={analyticsEnabled}
                        onChange={setAnalyticsEnabled}
                        style={{ marginLeft: 16 }}
                    />
                </div>

                <Divider style={{ margin: '16px 0' }} />

                {/* Marketing Cookies */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <Text strong>Marketing Cookies</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '13px' }}>
                            Used to track visitors across websites to display relevant advertisements
                            and measure the effectiveness of marketing campaigns.
                        </Text>
                    </div>
                    <Switch
                        checked={marketingEnabled}
                        onChange={setMarketingEnabled}
                        style={{ marginLeft: 16 }}
                    />
                </div>

                <Divider style={{ margin: '16px 0' }} />

                {/* Consent Status */}
                {consentData?.hasConsent && consentData.consentGivenAt && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Last updated: {new Date(consentData.consentGivenAt).toLocaleString()}
                    </Text>
                )}

                {/* Save Button */}
                <div style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        onClick={handleSave}
                        loading={saving}
                        disabled={!hasChanges}
                    >
                        Save Preferences
                    </Button>
                </div>
            </Space>
        </Card>
    );
};

export default CookieSettings;
